import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  predictedFlareDates,
  predictedPeriodDates,
  toISODate,
  type CyclePrediction,
  type DayLog,
} from '@/lib/cycle';

interface CycleCalendarProps {
  byDate: Map<string, DayLog>;
  prediction: CyclePrediction | null;
  selectedDate: string;
  onSelect: (iso: string) => void;
  /** Gentle mode: mark predicted period days (aqua) instead of flare-ups. */
  gentle?: boolean;
  /** Per-day plans (date ISO → plan text); days with plans get a marker. */
  events?: Record<string, string>;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_STEP_MIN = -6;
const MONTH_STEP_MAX = 4;

function painDotClass(pain: number): string {
  if (pain <= 0) return 'bg-muted-foreground/30';
  if (pain <= 3) return 'bg-lavender-strong/60';
  if (pain <= 6) return 'bg-lavender-strong';
  return 'bg-blush-strong';
}

export function CycleCalendar({
  byDate,
  prediction,
  selectedDate,
  onSelect,
  gentle = false,
  events = {},
}: CycleCalendarProps) {
  const todayISO = toISODate(new Date());
  const [viewOffset, setViewOffset] = useState(0); // months from current month

  const viewDate = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + viewOffset, 1);
  }, [viewOffset]);

  const markedSet = useMemo(
    () =>
      prediction
        ? gentle
          ? predictedPeriodDates(prediction)
          : predictedFlareDates(prediction)
        : new Set<string>(),
    [prediction, gentle],
  );

  const cells = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const startWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const list: Array<{ iso: string; day: number } | null> = [];
    for (let i = 0; i < startWeekday; i++) list.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      list.push({ iso: toISODate(new Date(year, month, d)), day: d });
    }
    return list;
  }, [viewDate]);

  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <Card className="rounded-3xl shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Previous month"
            disabled={viewOffset <= MONTH_STEP_MIN}
            onClick={() => setViewOffset((v) => Math.max(MONTH_STEP_MIN, v - 1))}
            className="size-11 rounded-full hover:bg-lavender"
          >
            <ChevronLeft className="size-6" />
          </Button>
          <CardTitle className="text-xl font-bold">{monthLabel}</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Next month"
            disabled={viewOffset >= MONTH_STEP_MAX}
            onClick={() => setViewOffset((v) => Math.min(MONTH_STEP_MAX, v + 1))}
            className="size-11 rounded-full hover:bg-lavender"
          >
            <ChevronRight className="size-6" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-7 gap-1.5">
          {WEEKDAYS.map((d, i) => (
            <div
              key={`${d}-${i}`}
              className="pb-1 text-center text-xs font-bold uppercase tracking-wide text-muted-foreground"
            >
              {d}
            </div>
          ))}
          {cells.map((cell, i) => {
            if (!cell) return <div key={`empty-${i}`} />;
            const { iso, day } = cell;
            const log = byDate.get(iso);
            const isToday = iso === todayISO;
            const isSelected = iso === selectedDate;
            const isFuture = iso > todayISO;
            const isMarked = markedSet.has(iso);
            const hasPlan = Boolean(events[iso]);

            return (
              <button
                key={iso}
                type="button"
                onClick={() => onSelect(iso)}
                aria-label={`${iso}${isMarked ? (gentle ? ', predicted period day' : ', predicted flare-up day') : ''}${log ? `, pain ${log.pain} of 10` : ''}`}
                aria-pressed={isSelected}
                className={cn(
                  'relative flex h-12 flex-col items-center justify-center rounded-xl border text-sm transition-colors sm:h-14',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isSelected
                    ? 'border-lavender-strong/60 bg-lavender'
                    : isMarked
                      ? gentle
                        ? 'border-dashed border-2 border-aqua-strong/50 bg-aqua/40 hover:bg-aqua/60'
                        : 'border-dashed border-2 border-blush-strong/50 bg-blush/40 hover:bg-blush/60'
                      : 'border-transparent hover:bg-lavender/50',
                  isToday && 'ring-2 ring-inset ring-aqua-strong',
                  isFuture && !isMarked && 'text-muted-foreground/50',
                )}
              >
                <span className={cn('font-semibold leading-none', log && 'text-foreground')}>
                  {day}
                </span>
                <span className="mt-1 flex h-1.5 items-center gap-1">
                  {log && (
                    <span
                      className={cn('size-1.5 rounded-full', painDotClass(log.pain))}
                      aria-hidden
                    />
                  )}
                  {hasPlan && (
                    <span className="size-1.5 rounded-full bg-amber-400" aria-hidden />
                  )}
                </span>
                {log && log.flow !== 'none' && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute bottom-1 h-1 w-5 rounded-full bg-blush-strong',
                      log.flow === 'light' && 'opacity-40',
                      log.flow === 'medium' && 'opacity-70',
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-lavender-strong" aria-hidden /> Pain logged
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1 w-4 rounded-full bg-blush-strong" aria-hidden /> Flow
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-amber-400" aria-hidden /> Plan
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className={cn(
                'size-3.5 rounded-md border-2 border-dashed',
                gentle ? 'border-aqua-strong/60 bg-aqua/40' : 'border-blush-strong/60 bg-blush/40',
              )}
              aria-hidden
            />
            {gentle ? 'Predicted period' : 'Predicted flare-up'}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-3.5 rounded-md ring-2 ring-inset ring-aqua-strong" aria-hidden />
            Today
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
